import { useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useLocalization } from "../contexts/LocalizationContext";
import { api } from "../api";
import { routes } from "../config";

type Gender = "M" | "F" | "OTHER" | "";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: "18px" }}>
      <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#8A8A8A", mb: "6px", letterSpacing: "0.3px", textTransform: "uppercase" }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

const inputSx = {
  width: "100%",
  fontSize: "15px",
  fontWeight: 500,
  color: "#1A1A1A",
  background: "#F8F8F8",
  border: "1px solid #E8E8E8",
  borderRadius: "10px",
  px: "14px",
  py: "11px",
  fontFamily: "inherit",
  outline: "none",
  boxSizing: "border-box" as const,
  "&:focus": { borderColor: "#7BA88A", background: "#fff" },
};

export default function AccountPage() {
  const { customer, updateCustomer, signOut } = useAuth();
  const navigate = useNavigate();
  const { t } = useLocalization();

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [firstName, setFirstName] = useState(customer?.firstName ?? "");
  const [lastName, setLastName] = useState(customer?.lastName ?? "");
  const [dob, setDob] = useState(customer?.dob ?? "");
  const [gender, setGender] = useState<Gender>((customer?.gender as Gender) ?? "");
  const [saveError, setSaveError] = useState("");


  const handleSave = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const { data } = await api.patch("/api/members/me", {
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        dob: dob || undefined,
        gender: gender || undefined,
      });
      updateCustomer(data);
      setEditing(false);
    } catch {
      setSaveError(t("account.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate(routes.login, { replace: true });
  };

  const handleCancel = () => {
    setFirstName(customer?.firstName ?? "");
    setLastName(customer?.lastName ?? "");
    setDob(customer?.dob ?? "");
    setGender((customer?.gender as Gender) ?? "");
    setSaveError("");
    setEditing(false);
  };

  return (
    <Box sx={{ flex: 1, backgroundColor: "#F6F6F6", display: "flex", flexDirection: "column", pb: '30px' }}>
      {/* Header */}
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        px: 2, py: 1.5, position: "sticky", top: 0,
        backgroundColor: "#fff", zIndex: 5, borderBottom: "1px solid #F0F0F0",
      }}>
        <Typography sx={{ fontSize: "17px", fontWeight: 700, color: "#1A1A1A" }}>
          {t("account.title")}
        </Typography>
        {!editing && (
          <Box
            component="button"
            onClick={() => setEditing(true)}
            sx={{
              border: "none", background: "none", cursor: "pointer", fontFamily: "inherit",
              fontSize: "14px", fontWeight: 600, color: "#7BA88A", p: "6px 2px",
              "&:active": { opacity: 0.65 },
            }}
          >
            {t("account.edit")}
          </Box>
        )}
      </Box>

      {/* Membership card */}
      <Box sx={{ mx: 2, mt: 2, background: "#fff", borderRadius: "16px", p: "16px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", mb: 2 }}>
        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#1A1A1A", mb: "10px" }}>
          {t("account.membership")}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: "8px" }}>
          <Typography sx={{ fontSize: "13px", color: "#8A8A8A" }}>{t("home.points.available")}</Typography>
          <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#1A1A1A" }}>
            {(customer?.pointsBalance ?? 0).toLocaleString()}
          </Typography>
        </Box>
        {customer?.nextMembershipLevel && customer.nextMembershipLevel.requiredSpendingAmountToNextLevel > 0 && (
          <>
            <Box sx={{ height: 5, background: "#E8E8E8", borderRadius: "99px", overflow: "hidden", mb: "6px" }}>
              <Box sx={{ height: "100%", width: `${customer.nextMembershipLevel.progressToNextLevel}%`, background: "#7BA88A", borderRadius: "99px" }} />
            </Box>
            <Typography sx={{ fontSize: "12px", color: "#8A8A8A" }}>
              HK${customer.nextMembershipLevel.requiredSpendingAmountToNextLevel.toLocaleString()} {t("account.toNextLevel")} {customer.nextMembershipLevel.nextMembershipLevelName}
            </Typography>
          </>
        )}
      </Box>

      {/* Profile fields */}
      <Box sx={{ mx: 2, background: "#fff", borderRadius: "16px", p: "20px 16px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", mb: 2 }}>
        {editing ? (
          <>
            <Field label={t("account.fields.firstName")}>
              <Box component="input" value={firstName} onChange={e => setFirstName(e.target.value)} sx={inputSx} />
            </Field>
            <Field label={t("account.fields.lastName")}>
              <Box component="input" value={lastName} onChange={e => setLastName(e.target.value)} sx={inputSx} />
            </Field>
            <Field label={t("account.fields.dob")}>
              <Box component="input" type="date" value={dob} onChange={e => setDob(e.target.value)} sx={{ ...inputSx, colorScheme: "light" }} />
            </Field>
            <Field label={t("account.fields.gender")}>
              <Box sx={{ display: "flex", gap: "8px" }}>
                {(["M", "F", "OTHER"] as Gender[]).map(g => (
                  <Box
                    key={g}
                    component="button"
                    onClick={() => setGender(g === gender ? "" : g)}
                    sx={{
                      flex: 1, border: "1px solid", borderColor: gender === g ? "#7BA88A" : "#E0E0E0",
                      background: gender === g ? "#F0F7F3" : "#F8F8F8",
                      color: gender === g ? "#5A8A6C" : "#555",
                      fontWeight: gender === g ? 700 : 500,
                      borderRadius: "10px", py: "10px", fontSize: "13px",
                      cursor: "pointer", fontFamily: "inherit",
                      "&:active": { opacity: 0.75 },
                    }}
                  >
                    {g === "M" ? t("account.gender.male") : g === "F" ? t("account.gender.female") : t("account.gender.other")}
                  </Box>
                ))}
              </Box>
            </Field>
            {saveError && (
              <Typography sx={{ fontSize: "13px", color: "#E85A22", mb: "12px" }}>{saveError}</Typography>
            )}
            <Box sx={{ display: "flex", gap: "10px", mt: "4px" }}>
              <Box
                component="button"
                onClick={handleCancel}
                sx={{
                  flex: 1, py: "12px", borderRadius: "12px",
                  border: "1px solid #E0E0E0", background: "#fff",
                  fontSize: "14px", fontWeight: 600, color: "#555",
                  cursor: "pointer", fontFamily: "inherit",
                  "&:active": { background: "#F5F5F5" },
                }}
              >
                {t("account.cancel")}
              </Box>
              <Box
                component="button"
                onClick={handleSave}
                disabled={saving}
                sx={{
                  flex: 1, py: "12px", borderRadius: "12px",
                  border: "none", background: "#7BA88A",
                  fontSize: "14px", fontWeight: 700, color: "#fff",
                  cursor: "pointer", fontFamily: "inherit",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                  "&:active": { opacity: 0.85 },
                  "&:disabled": { opacity: 0.6 },
                }}
              >
                {saving && <CircularProgress size={14} sx={{ color: "#fff" }} />}
                {t("account.save")}
              </Box>
            </Box>
          </>
        ) : (
          <>
            {[
              { label: t("account.fields.firstName"), value: customer?.firstName },
              { label: t("account.fields.lastName"), value: customer?.lastName },
              { label: t("account.fields.email"), value: customer?.email },
              { label: t("account.fields.phone"), value: customer?.phone },
              { label: t("account.fields.dob"), value: customer?.dob },
              { label: t("account.fields.gender"), value: customer?.gender === "M" ? t("account.gender.male") : customer?.gender === "F" ? t("account.gender.female") : customer?.gender === "OTHER" ? t("account.gender.other") : undefined },
            ].map(({ label, value }) => (
              <Box key={label} sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", py: "11px", borderBottom: "1px solid #F3F3F3", "&:last-child": { borderBottom: "none" } }}>
                <Typography sx={{ fontSize: "13px", color: "#8A8A8A", fontWeight: 500, flexShrink: 0, mr: "10%" }}>{label}</Typography>
                <Typography sx={{ fontSize: "14px", color: "#1A1A1A", fontWeight: 500, textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: 1 }}>
                  {value || "—"}
                </Typography>
              </Box>
            ))}
          </>
        )}
      </Box>


      {/* Referral code */}
      {customer?.referralCode && (
        <Box sx={{ mx: 2, background: "#fff", borderRadius: "16px", p: "16px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontSize: "13px", color: "#8A8A8A", fontWeight: 500 }}>{t("account.referralCode")}</Typography>
          <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#7BA88A", letterSpacing: "1px" }}>
            {customer.referralCode}
          </Typography>
        </Box>
      )}

      {/* Logout */}
      <Box sx={{ mx: 2, mt: "auto" }}>
        <Box
          component="button"
          onClick={handleLogout}
          sx={{
            width: "100%", py: "14px", borderRadius: "14px",
            border: "1.5px solid #FFCFCF", background: "#FFF5F5",
            fontSize: "15px", fontWeight: 700, color: "#E05050",
            cursor: "pointer", fontFamily: "inherit",
            display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
            transition: "background 0.12s",
            "&:active": { background: "#FFE8E8" },
          }}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            <path d="M16 17l5-5-5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M21 12H9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
          {t("account.logout")}
        </Box>
      </Box>
    </Box>
  );
}
